import i18n from '@/i18n';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const allowedLangs = ['en', 'es', 'fr', 'ca'] as const;
export type AllowedLang = (typeof allowedLangs)[number];

function isAllowedLang(lang: string): lang is AllowedLang {
  return allowedLangs.includes(lang as AllowedLang);
}

interface ContextTypes {
  userLanguage: AllowedLang;
  allowedLangs: AllowedLang[];
  onChangeLanguage: (language: AllowedLang) => void;
}

const UserInfoContext = createContext<ContextTypes | undefined>(undefined);

export const UserInfoProvider = ({ children }: React.PropsWithChildren) => {
  const [lang, setLang] = useState<AllowedLang>('en');

  useEffect(() => {
    let initialLang = detectLanguage();
    // If URL lang is different from localStorage/state, update localStorage and state
    setLang(initialLang);
    localStorage.setItem('userLanguage', initialLang);
    i18n.changeLanguage(initialLang);
  }, []);

  useEffect(() => {
    if (!lang) {
      return;
    }
    // Sync localStorage and i18n
    localStorage.setItem('userLanguage', lang);
    i18n.changeLanguage(lang);
    // Sync URL if needed (only if not already present)
    syncLanguageToUrl(lang);
  }, [lang]);

  useEffect(() => {
    if (lang) {
      localStorage.setItem('userLanguage', lang);
    }
    i18n.changeLanguage(lang);
  }, [lang]);

  const handleChangeLanguage = useCallback((language: AllowedLang) => {
    const newLang = isAllowedLang(language) ? language : 'en';
    setLang(newLang);
  }, []);

  const value = useMemo(
    () => ({
      userLanguage: lang,
      allowedLangs: [...allowedLangs],
      onChangeLanguage: handleChangeLanguage,
    }),
    [lang, handleChangeLanguage],
  );

  return <UserInfoContext.Provider value={value}>{children}</UserInfoContext.Provider>;
};

export const useUserInfo = () => {
  const context = useContext(UserInfoContext);
  if (!context) {
    throw new Error('useUserInfo must be used in a UserInfoProvider');
  }
  return context;
};

/**
 * Detect language from URL, localStorage, or browser
 */
function detectLanguage() {
  const pathParts = window.location.pathname.split('/');
  const urlLang = isAllowedLang(pathParts[1])
    ? (pathParts[1])
    : null;
  const storedLang = localStorage.getItem('userLanguage');
  const navigatorLanguage = navigator.language.split('-')[0];
  let initialLang = urlLang || storedLang || navigatorLanguage;
  if (!isAllowedLang(initialLang)) {
    return 'en';
  }
  return initialLang;
}

function syncLanguageToUrl(lang: string) {
  const pathParts = window.location.pathname.split('/');
  const currentLangInUrl = isAllowedLang(pathParts[1])
    ? (pathParts[1])
    : null;
  if (currentLangInUrl === lang) return;
  let newPath: string;
  if (currentLangInUrl) {
    // Elimina barra final si existe
    const restPath = window.location.pathname.substring(3).replace(/\/$/, '');
    newPath = `/${lang}${restPath}`;
  } else {
    const restPath = window.location.pathname.replace(/\/$/, '');
    newPath = `/${lang}${restPath}`;
  }
  window.history.replaceState(
    null,
    '',
    newPath + window.location.search + window.location.hash
  );
}
